import { useEffect, useState, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Swords, Trophy, Zap, Crown, TrendingUp, TrendingDown, Lock, Bot as BotIcon, RotateCcw, Share2, CalendarDays, Sparkles, Layers } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/lib/i18n";
import { WvTutorial, SolTutorial, DailyWordVolt, DailySolitaire } from "@/components/DailyWordVolt";
import { shareWinCard } from "@/lib/shareCard";
import { useAudio, useTrack } from "@/context/GameFeel";
import { VoltSay } from "@/components/Volt";
import { intro, personaFor, quip, moodFor } from "@/lib/volt";

const solOk = (a, b) => { const d = Math.abs(a - b); return d === 1 || d === 12; };

export function DuelArena({ duel, opponent, onClose, refresh, onRematch, onChallenge, practice = false, game = null }) {
  const { t } = useT();
  const { sfx } = useAudio();
  useTrack("play");
  const [announce] = useState(() => practice ? intro(personaFor(game)) : intro("duel", { opp: opponent?.name || "a challenger" }));
  const [voltLine, setVoltLine] = useState(null);
  const [showAnnounce, setShowAnnounce] = useState(true);
  useEffect(() => { const to = setTimeout(() => setShowAnnounce(false), 9000); return () => clearTimeout(to); }, []);
  const [session, setSession] = useState(null);
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [input, setInput] = useState("");
  const [time, setTime] = useState(45);
  const [phase, setPhase] = useState("countdown"); // countdown, play, done
  const [result, setResult] = useState(null);
  const [count, setCount] = useState(3);
  const timerRef = useRef(null);
  const isReels = session?.game === "skill_reels";
  const isReaction = session?.game === "reaction_rush";
  const isMemory = session?.game === "memory_matrix";
  const isCard21 = session?.game === "card_21";
  const isWheel = session?.game === "precision_wheel";
  const isWord = session?.game === "word_volt";
  const isSol = session?.game === "solitaire_sprint";
  const [solChain, setSolChain] = useState([]);
  const solCur = session?.board ? (solChain.length ? session.board.cards[solChain[solChain.length - 1]].r : session.board.start.r) : null;
  const isBoard = isReels || isReaction || isMemory || isCard21 || isWheel;
  const REEL_EMOJI = { seven: "7️⃣", bar: "🅱️", cherry: "🍒", bell: "🔔", star: "⭐", diamond: "💎" };
  const [rIdx, setRIdx] = useState(0);
  const [sel, setSel] = useState([0, 0, 0]);
  const [solved, setSolved] = useState({});
  const [pickedMem, setPickedMem] = useState([]);
  const [showPat, setShowPat] = useState(false);
  const [pick21, setPick21] = useState([]);
  const [wheelSteps, setWheelSteps] = useState(0);
  const [wvWords, setWvWords] = useState([]);
  const [wvInput, setWvInput] = useState("");
  const [wvMsg, setWvMsg] = useState(null);
  const wvTotal = wvWords.reduce((s, w) => s + w.points, 0);
  const [showTut, setShowTut] = useState(() => { try { return !localStorage.getItem("wv_tut_seen"); } catch { return false; } });
  const dismissTut = () => { try { localStorage.setItem("wv_tut_seen", "1"); } catch { /* noop */ } setShowTut(false); };
  const [showSolTut, setShowSolTut] = useState(() => { try { return !localStorage.getItem("sol_tut_seen"); } catch { return false; } });
  const dismissSolTut = () => { try { localStorage.setItem("sol_tut_seen", "1"); } catch { /* noop */ } setShowSolTut(false); };

  useEffect(() => {
    if (count <= 0) { sfx("whoosh"); start(); return; }
    sfx("tick");
    const to = setTimeout(() => setCount((c) => c - 1), 700);
    return () => clearTimeout(to);
  }, [count]);

  const start = async () => {
    try {
      const { data } = practice
        ? await api.post(`/duels/practice-start`, { game })
        : await api.post(`/duels/${duel}/play`);
      setSession(data); setTime(data.duration); setPhase("play");
      if (data.game === "skill_reels" && data.rounds?.length) { setRIdx(0); setSel(data.rounds[0].start); setSolved({}); }
      if (data.game === "reaction_rush" || data.game === "memory_matrix") { setRIdx(0); setSolved({}); setPickedMem([]); }
      if (data.game === "card_21") { setRIdx(0); setSolved({}); setPick21([]); }
      if (data.game === "precision_wheel") { setRIdx(0); setSolved({}); setWheelSteps(0); }
      if (data.game === "word_volt") { setWvWords([]); setWvInput(""); setWvMsg(null); }
      if (data.game === "solitaire_sprint") setSolChain([]);
    } catch (e) { toast.error(apiErr(e)); onClose(); }
  };

  const doSubmit = async (ans) => {
    const url = practice ? `/duels/practice-submit` : `/duels/${duel}/submit`;
    const { data } = await api.post(url, { session_id: session.session_id, answers: ans });
    setResult(data); if (!practice) refresh();
    const persona = personaFor(session?.game || game);
    if (data.practice) { sfx("fanfare"); setVoltLine({ text: quip({ outcome: "win", persona }), mood: "happy" }); }
    else if (data.outcome) {
      sfx(data.outcome === "win" ? "fanfare" : data.outcome === "loss" ? "wrong" : "tap");
      if (data.outcome === "win") setTimeout(() => sfx("coin"), 350);
      setVoltLine({ text: quip({ outcome: data.outcome, persona }), mood: moodFor(data.outcome) });
    }
  };

  // Memory: flash the pattern briefly at the start of each round
  useEffect(() => {
    if (phase === "play" && isMemory && session?.rounds?.[rIdx]) {
      setShowPat(true); setPickedMem([]);
      const to = setTimeout(() => setShowPat(false), 1300);
      return () => clearTimeout(to);
    }
  }, [phase, isMemory, rIdx, session]);

  const advance = (na) => {
    if (rIdx < session.rounds.length - 1) setRIdx(rIdx + 1);
    else submitReels(na);
  };
  const tapReaction = (cell) => {
    sfx("tap");
    const na = { ...solved, [rIdx]: cell }; setSolved(na); advance(na);
  };
  const submitMemRound = () => {
    const na = { ...solved, [rIdx]: pickedMem }; setSolved(na); advance(na);
  };
  const toggleMem = (cell) => setPickedMem((p) => p.includes(cell) ? p.filter((x) => x !== cell) : [...p, cell]);

  const toggleCard = (i) => setPick21((p) => p.includes(i) ? p.filter((x) => x !== i) : [...p, i]);
  const lockCard21 = () => {
    const na = { ...solved, [rIdx]: pick21 }; setSolved(na); setPick21([]);
    if (rIdx < session.rounds.length - 1) setRIdx(rIdx + 1); else submitReels(na);
  };
  const stepWheel = () => {
    const rd = session.rounds[rIdx];
    const ns = wheelSteps + 1; setWheelSteps(ns);
    if ((rd.start + ns) % rd.segments === rd.target) {
      const na = { ...solved, [rIdx]: ns }; setSolved(na); setWheelSteps(0);
      if (rIdx < session.rounds.length - 1) setRIdx(rIdx + 1); else submitReels(na);
    }
  };
  const lockWheel = () => {
    const na = { ...solved, [rIdx]: wheelSteps }; setSolved(na); setWheelSteps(0);
    if (rIdx < session.rounds.length - 1) setRIdx(rIdx + 1); else submitReels(na);
  };

  const submitReels = async (ans = solved) => {
    clearInterval(timerRef.current); setPhase("done");
    try { await doSubmit(ans); } catch (e) { toast.error(apiErr(e)); }
  };

  const nudge = (reel, dir) => {
    const rd = session.rounds[rIdx];
    const ns = [...sel]; ns[reel] = (ns[reel] + dir + 5) % 5; setSel(ns);
    if (rd.reels.every((strip, k) => strip[ns[k]] === rd.target)) {
      const na = { ...solved, [rIdx]: ns }; setSolved(na);
      if (rIdx < session.rounds.length - 1) { const nx = rIdx + 1; setRIdx(nx); setSel(session.rounds[nx].start); }
      else submitReels(na);
    }
  };

  useEffect(() => {
    if (phase !== "play") return;
    if (isWord && showTut) return; // paused until tutorial dismissed
    if (isSol && showSolTut) return; // paused until solitaire tutorial dismissed
    timerRef.current = setInterval(() => setTime((x) => x - 1), 1000);
    return () => clearInterval(timerRef.current);
  }, [phase, isWord, showTut, isSol, showSolTut]);
  useEffect(() => { if (time <= 0 && phase === "play") { isWord ? submitWords() : isSol ? submitSol() : (isBoard ? submitReels() : submit()); } }, [time]);

  const opSym = { "+": "+", "-": "−", x: "×" };
  const cur = session?.problems?.[idx];
  const answer = (val) => {
    sfx("tap");
    setAnswers({ ...answers, [idx]: parseInt(val) }); setInput("");
    if (idx < session.problems.length - 1) setIdx(idx + 1);
  };
  const submit = async () => {
    clearInterval(timerRef.current); setPhase("done");
    try { await doSubmit(answers); } catch (e) { toast.error(apiErr(e)); }
  };

  const addWord = async () => {
    const w = wvInput.trim().toUpperCase();
    setWvInput("");
    if (w.length < 3) { setWvMsg({ ok: false, text: "Min 3 letters" }); return; }
    if (wvWords.some((x) => x.word === w)) { setWvMsg({ ok: false, text: `${w} already used` }); return; }
    try {
      const { data } = await api.post("/duels/wordvolt/check", { session_id: session.session_id, word: w });
      if (data.valid) { sfx("correct"); setWvWords((p) => [{ word: data.word, points: data.points }, ...p]); setWvMsg({ ok: true, text: `${data.word} +${data.points}` }); }
      else { sfx("wrong"); setWvMsg({ ok: false, text: `${w}: ${data.reason}` }); }
    } catch { setWvMsg({ ok: false, text: "Could not check word" }); }
  };
  const submitWords = async () => {
    clearInterval(timerRef.current); setPhase("done");
    try { await doSubmit({ words: wvWords.map((w) => w.word) }); } catch (e) { toast.error(apiErr(e)); }
  };
  const tapSolCard = (i) => {
    if (solChain.includes(i)) return;
    if (!solOk(session.board.cards[i].r, solCur)) { sfx("wrong"); return; }
    sfx("correct");
    setSolChain((p) => [...p, i]);
  };
  const submitSol = async () => {
    clearInterval(timerRef.current); setPhase("done");
    try { await doSubmit({ moves: solChain }); } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/85 backdrop-blur-md flex items-center justify-center p-4">
      <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
        className="w-full max-w-lg glass rounded-3xl border border-emerald-500/30 glow-cash p-8 relative">
        {isWord && phase === "play" && showTut && <WvTutorial onClose={dismissTut} />}
        {isSol && phase === "play" && showSolTut && <SolTutorial onClose={dismissSolTut} />}
        {/* VS header */}
        <div className="flex items-center justify-between mb-6">
          <div className="text-center flex-1">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-emerald-400 to-cyan-600 mx-auto flex items-center justify-center font-bold">YOU</div>
          </div>
          <div className="font-display font-black text-2xl text-emerald-400 px-3">{t("vs")}</div>
          <div className="text-center flex-1">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-fuchsia-500 to-purple-700 mx-auto flex items-center justify-center">
              {opponent?.is_bot ? <BotIcon className="w-6 h-6" /> : <span className="font-bold text-sm">{opponent?.name?.slice(0, 2)}</span>}
            </div>
            <p className="text-xs text-gray-400 mt-1">{opponent?.name} · {opponent?.elo}</p>
          </div>
        </div>

        {phase === "countdown" && (
          <div className="text-center py-6">
            <div className="font-display font-black text-7xl text-emerald-400 mb-6">{count || t("ready")}</div>
            <VoltSay testid="volt-duel-intro" text={announce} mood="cheer" label={practice ? "Volt · coach" : "Volt · announcer"} accent="emerald" size={56} />
          </div>
        )}
        {phase === "play" && showAnnounce && (
          <div className="mb-4"><VoltSay testid="volt-duel-intro" text={announce} mood="happy" label={practice ? "Volt · coach" : "Volt · announcer"} accent="emerald" size={36} /></div>
        )}

        {phase === "play" && isReaction && session?.rounds?.[rIdx] && (
          <div className="text-center">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm text-gray-500">{rIdx + 1}/{session.rounds.length}</span>
              <span className={`font-mono text-2xl font-black ${time <= 8 ? "text-red-400" : "text-emerald-300"}`}>{time}s</span>
            </div>
            <p className="text-sm text-gray-400 mb-3">Tap the lit cell — fast!</p>
            <div className="grid grid-cols-3 gap-2 max-w-[240px] mx-auto">
              {Array.from({ length: 9 }).map((_, c) => (
                <button key={c} data-testid={`reaction-cell-${c}`} onClick={() => tapReaction(c)}
                  className={`aspect-square rounded-xl border transition-colors duration-100 ${c === session.rounds[rIdx].target ? "bg-emerald-500 border-emerald-300 shadow-[0_0_20px_rgba(16,185,129,0.7)]" : "bg-black/40 border-white/10"}`} />
              ))}
            </div>
          </div>
        )}

        {phase === "play" && isMemory && session?.rounds?.[rIdx] && (
          <div className="text-center">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm text-gray-500">{rIdx + 1}/{session.rounds.length} · {pickedMem.length}/{session.rounds[rIdx].pattern.length}</span>
              <span className={`font-mono text-2xl font-black ${time <= 8 ? "text-red-400" : "text-emerald-300"}`}>{time}s</span>
            </div>
            <p className="text-sm text-gray-400 mb-3">{showPat ? "Memorize the pattern…" : "Recreate the pattern"}</p>
            <div className="grid grid-cols-3 gap-2 max-w-[240px] mx-auto mb-4">
              {Array.from({ length: 9 }).map((_, c) => {
                const lit = showPat && session.rounds[rIdx].pattern.includes(c);
                const picked = pickedMem.includes(c);
                return (
                  <button key={c} data-testid={`memory-cell-${c}`} disabled={showPat} onClick={() => toggleMem(c)}
                    className={`aspect-square rounded-xl border transition-colors duration-150 ${lit ? "bg-cyan-400 border-cyan-200" : picked ? "bg-emerald-500/60 border-emerald-400" : "bg-black/40 border-white/10"}`} />
                );
              })}
            </div>
            {!showPat && <button data-testid="memory-submit" onClick={submitMemRound} className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-emerald-500 to-cyan-600 font-semibold">Lock In</button>}
          </div>
        )}

        {phase === "play" && isReels && session?.rounds?.[rIdx] && (
          <div className="text-center">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm text-gray-500">{Object.keys(solved).length}/{session.rounds.length} solved</span>
              <span className={`font-mono text-2xl font-black ${time <= 10 ? "text-red-400" : "text-emerald-300"}`}>{time}s</span>
            </div>
            <p className="text-sm text-gray-400 mb-3">Nudge all reels to <span className="text-2xl align-middle">{REEL_EMOJI[session.rounds[rIdx].target]}</span></p>
            <div className="flex justify-center gap-4 mb-2">
              {session.rounds[rIdx].reels.map((strip, k) => (
                <div key={k} className="flex flex-col items-center gap-2">
                  <button data-testid={`reel-up-${k}`} onClick={() => nudge(k, -1)} className="w-14 h-8 rounded-lg bg-white/10 hover:bg-white/20 transition-colors duration-150 text-lg">▲</button>
                  <div className={`w-16 h-16 rounded-xl flex items-center justify-center text-4xl border-2 ${strip[sel[k]] === session.rounds[rIdx].target ? "border-emerald-500/70 bg-emerald-500/10" : "border-amber-500/40 bg-black/50"}`}>
                    {REEL_EMOJI[strip[sel[k]]]}
                  </div>
                  <button data-testid={`reel-down-${k}`} onClick={() => nudge(k, 1)} className="w-14 h-8 rounded-lg bg-white/10 hover:bg-white/20 transition-colors duration-150 text-lg">▼</button>
                </div>
              ))}
            </div>
            <p className="text-xs text-gray-600 mt-3">Match the payline to score. Skill decides — no random payouts.</p>
          </div>
        )}

        {phase === "play" && isCard21 && session?.rounds?.[rIdx] && (() => {
          const rd = session.rounds[rIdx];
          const sum = pick21.reduce((s, i) => s + rd.cards[i], 0);
          const face = (v) => v === 11 ? "A" : String(v);
          return (
            <div className="text-center">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm text-gray-500">{Object.keys(solved).length}/{session.rounds.length} · total {sum}</span>
                <span className={`font-mono text-2xl font-black ${time <= 10 ? "text-red-400" : "text-emerald-300"}`}>{time}s</span>
              </div>
              <p className="text-sm text-gray-400 mb-3">Pick cards that total exactly <span className="text-emerald-300 font-bold">21</span></p>
              <div className="flex flex-wrap justify-center gap-2 mb-4">
                {rd.cards.map((v, i) => {
                  const on = pick21.includes(i);
                  return (
                    <button key={i} data-testid={`card21-card-${i}`} onClick={() => toggleCard(i)}
                      className={`w-14 h-20 rounded-lg border-2 flex items-center justify-center text-2xl font-black transition-colors duration-150 ${on ? "bg-emerald-500/25 border-emerald-400 text-emerald-200" : "bg-white/90 border-white/30 text-gray-900"}`}>
                      {face(v)}
                    </button>
                  );
                })}
              </div>
              <button data-testid="card21-lock" onClick={lockCard21}
                className={`px-6 py-2.5 rounded-xl font-semibold transition-colors duration-200 ${sum === 21 ? "bg-gradient-to-r from-emerald-500 to-cyan-600" : "bg-white/10 text-gray-300"}`}>
                {sum === 21 ? "Score 21!" : "Lock In / Skip"}
              </button>
              <p className="text-xs text-gray-600 mt-3">Pure skill — arithmetic under time. No random payouts.</p>
            </div>
          );
        })()}

        {phase === "play" && isWheel && session?.rounds?.[rIdx] && (() => {
          const rd = session.rounds[rIdx];
          const pos = (rd.start + wheelSteps) % rd.segments;
          return (
            <div className="text-center">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm text-gray-500">{Object.keys(solved).length}/{session.rounds.length}</span>
                <span className={`font-mono text-2xl font-black ${time <= 10 ? "text-red-400" : "text-emerald-300"}`}>{time}s</span>
              </div>
              <p className="text-sm text-gray-400 mb-3">Advance the pointer onto segment <span className="text-emerald-300 font-bold">{rd.target}</span></p>
              <div className="relative w-52 h-52 mx-auto mb-4">
                {Array.from({ length: rd.segments }).map((_, s) => {
                  const angle = (s / rd.segments) * 360;
                  const isTarget = s === rd.target;
                  const isPos = s === pos;
                  return (
                    <div key={s} style={{ transform: `rotate(${angle}deg) translateY(-92px)` }} className="absolute left-1/2 top-1/2 -ml-4 -mt-4">
                      <div style={{ transform: `rotate(${-angle}deg)` }}
                        className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${isPos ? "bg-amber-400 text-black" : isTarget ? "bg-emerald-500/40 text-emerald-100 border border-emerald-400" : "bg-black/50 text-gray-400 border border-white/10"}`}>{s}</div>
                    </div>
                  );
                })}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-24 h-24 rounded-full border-2 border-emerald-500/30 flex items-center justify-center font-mono text-3xl font-black text-amber-300">{pos}</div>
                </div>
              </div>
              <div className="flex gap-2 justify-center">
                <button data-testid="wheel-step" onClick={stepWheel} className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-emerald-500 to-cyan-600 font-semibold">Spin +1</button>
                <button data-testid="wheel-lock" onClick={lockWheel} className="px-5 py-2.5 rounded-xl bg-white/10 border border-white/10 font-semibold">Skip</button>
              </div>
              <p className="text-xs text-gray-600 mt-3">Skill timing &amp; counting — you control the outcome.</p>
            </div>
          );
        })()}

        {phase === "play" && isSol && session?.board && (
          <div className="text-center">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm text-gray-500">Chain: <span className="text-emerald-300 font-mono">{solChain.length}</span></span>
              <span className={`font-mono text-2xl font-black ${time <= 10 ? "text-red-400" : "text-emerald-300"}`}>{time}s</span>
            </div>
            <p className="text-xs text-gray-500 mb-2">Tap a card one rank above or below the active card (A wraps to K).</p>
            <div className="mb-3 flex justify-center">
              <div data-testid="sol-active" className="w-16 h-20 rounded-xl bg-gradient-to-br from-emerald-500 to-cyan-600 flex items-center justify-center text-3xl font-black text-white shadow-lg">{session.board.cards[solChain[solChain.length - 1]]?.label ?? session.board.start.label}</div>
            </div>
            <div className="grid grid-cols-6 gap-1.5 mb-4">
              {session.board.cards.map((c, i) => {
                const used = solChain.includes(i);
                const playable = !used && solOk(c.r, solCur);
                return (
                  <button key={i} data-testid={`sol-card-${i}`} onClick={() => tapSolCard(i)} disabled={used || !playable}
                    className={`h-12 rounded-lg text-lg font-black transition-all duration-150 ${used ? "opacity-15 bg-white/5" : playable ? "bg-white/10 border-2 border-emerald-500/60 text-emerald-100 hover:scale-105" : "bg-white/5 border border-white/10 text-gray-500"}`}>
                    {c.label}
                  </button>
                );
              })}
            </div>
            <button data-testid="sol-done" onClick={submitSol} className="px-6 py-2.5 rounded-xl bg-white/10 border border-white/10 font-semibold">Finish</button>
            <p className="text-xs text-gray-600 mt-3">Longest chain when time's up wins. Same deal for both players.</p>
          </div>
        )}

        {phase === "play" && isWord && session?.rack && (
          <div className="text-center">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm text-gray-500">{wvWords.length} words · <span className="text-emerald-300 font-mono">{wvTotal} pts</span></span>
              <span className={`font-mono text-2xl font-black ${time <= 10 ? "text-red-400" : "text-emerald-300"}`}>{time}s</span>
            </div>
            <p className="text-sm text-gray-400 mb-3">Build words from these letters</p>
            <div className="flex justify-center gap-2 mb-4 flex-wrap">
              {session.rack.map((ch, i) => (
                <div key={i} data-testid={`wv-tile-${i}`}
                  className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500/20 to-cyan-600/20 border-2 border-emerald-500/40 flex items-center justify-center text-2xl font-black text-emerald-100">{ch}</div>
              ))}
            </div>
            <input data-testid="wv-input" autoFocus value={wvInput}
              onChange={(e) => setWvInput(e.target.value.replace(/[^a-zA-Z]/g, ""))}
              onKeyDown={(e) => { if (e.key === "Enter") addWord(); }}
              className="w-56 text-center uppercase tracking-widest bg-black/50 border-2 border-emerald-500/40 rounded-xl px-4 py-3 text-2xl font-mono focus:border-emerald-500 outline-none mb-2" placeholder="TYPE" />
            <div className="h-5 mb-2">{wvMsg && <span className={`text-sm font-mono ${wvMsg.ok ? "text-emerald-400" : "text-red-400"}`}>{wvMsg.text}</span>}</div>
            <div className="flex gap-2 justify-center mb-3">
              <button data-testid="wv-add" onClick={addWord} className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-emerald-500 to-cyan-600 font-semibold">Submit word</button>
              <button data-testid="wv-done" onClick={submitWords} className="px-5 py-2.5 rounded-xl bg-white/10 border border-white/10 font-semibold">Done</button>
            </div>
            <div className="flex flex-wrap justify-center gap-1.5 max-h-24 overflow-y-auto hide-scroll">
              {wvWords.map((w) => (
                <span key={w.word} className="px-2 py-1 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-xs font-mono text-emerald-200">{w.word} <span className="text-emerald-400">+{w.points}</span></span>
              ))}
            </div>
            <p className="text-xs text-gray-600 mt-3">Longer words score more. Same letters for both players — pure vocabulary skill.</p>
          </div>
        )}

        {phase === "play" && !isBoard && !isWord && !isSol && cur && (
          <div className="text-center">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm text-gray-500">{Object.keys(answers).length}/{session.problems.length}</span>
              <span className={`font-mono text-2xl font-black ${time <= 10 ? "text-red-400" : "text-emerald-300"}`}>{time}s</span>
            </div>
            <div className="font-mono font-black text-5xl sm:text-6xl mb-6">{cur.a} {opSym[cur.op]} {cur.b}</div>
            <input data-testid="duel-answer-input" autoFocus type="number" value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && input !== "") answer(input); }}
              className="w-40 text-center bg-black/50 border-2 border-emerald-500/40 rounded-xl px-4 py-4 text-3xl font-mono focus:border-emerald-500 outline-none mb-4" placeholder="?" />
            <button onClick={() => answer(input || 0)} data-testid="duel-answer-submit" disabled={input === ""}
              className="w-full py-3 rounded-xl bg-gradient-to-r from-emerald-500 to-cyan-600 font-bold disabled:opacity-40">{t("play")}</button>
          </div>
        )}

        {phase === "done" && result?.practice && (
          <div className="text-center py-4">
            <div className="text-5xl mb-3">🎯</div>
            <h3 className="font-display text-3xl font-black mb-2">Practice complete</h3>
            <p className="text-gray-400 mb-2">
              {result.total == null
                ? <>You scored <span className="text-emerald-300 font-mono font-bold">{result.score} pts</span>{result.word_count != null ? ` · ${result.word_count} words` : ""}</>
                : <>You scored <span className="text-emerald-300 font-mono font-bold">{result.score}/{result.total}</span></>}
            </p>
            <p className="text-xs text-gray-500 mb-5">Nothing was staked — this was a free practice run.</p>
            {voltLine && <div className="mb-5"><VoltSay testid="volt-duel-result" text={voltLine.text} mood={voltLine.mood} label="Volt · coach" accent="emerald" size={52} /></div>}
            <button data-testid="duel-close" onClick={onClose} className="px-6 py-3 rounded-xl bg-gradient-to-r from-emerald-500 to-cyan-600 font-semibold">Back</button>
          </div>
        )}

        {phase === "done" && !result?.practice && (
          <div className="text-center py-2">
            {result?.status === "waiting_for_opponent" ? (
              <><p className="text-gray-400 py-8">Your score: {result.your_score}. Waiting for opponent…</p></>
            ) : (
              <>
                <div className={`text-5xl mb-3`}>{result?.outcome === "win" ? "🏆" : result?.outcome === "draw" ? "🤝" : "💔"}</div>
                <h3 className="font-display text-3xl font-black mb-2">
                  {result?.outcome === "win" ? t("win") : result?.outcome === "draw" ? t("draw") : t("loss")}
                </h3>
                <p className="text-gray-400 mb-1">You {result?.your_score} — {result?.opponent_score} {result?.opponent_name}</p>
                {result?.outcome === "win" && <p data-testid="duel-prize" className="text-emerald-300 font-mono mb-1">{result.currency === "coins" ? `+${Number(result.prize).toLocaleString()} coins` : `+$${result.prize} cash`}</p>}
                {result?.elo_delta != null && (
                  <p className={`font-mono ${result.elo_delta >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                    ELO {result.elo_old} → {result.elo_new} ({result.elo_delta >= 0 ? "+" : ""}{result.elo_delta})
                  </p>
                )}
                {voltLine && <div className="mt-4"><VoltSay testid="volt-duel-result" text={voltLine.text} mood={voltLine.mood} label="Volt · announcer" accent="emerald" size={52} /></div>}
              </>
            )}
            <div className="flex flex-wrap gap-2 justify-center mt-6">
              {result?.status !== "waiting_for_opponent" && (
                <>
                  {isWord && result?.your_score != null && (
                    <button data-testid="duel-share-score" onClick={() => shareWinCard({
                      score: result.your_score, accent: "#34d399",
                      subtitle: result?.outcome === "win" ? `Won ${result.your_score}–${result.opp_score}` : `Scored ${result.your_score}`,
                    })} className="px-5 py-3 rounded-xl bg-gradient-to-r from-emerald-500 to-cyan-600 font-semibold flex items-center gap-2"><Share2 className="w-4 h-4" /> Share score</button>
                  )}
                  <button data-testid="duel-rematch" onClick={onRematch}
                    className="px-5 py-3 rounded-xl bg-white/10 border border-white/10 font-semibold flex items-center gap-2"><RotateCcw className="w-4 h-4" /> {t("rematch")}</button>
                  <button data-testid="duel-challenge-friend" onClick={onChallenge}
                    className="px-5 py-3 rounded-xl bg-white/10 border border-white/10 font-semibold flex items-center gap-2"><Share2 className="w-4 h-4" /> {t("challenge_friend")}</button>
                </>
              )}
              <button data-testid="duel-close" onClick={onClose} className="px-5 py-3 rounded-xl bg-white/5 border border-white/10">{t("back")}</button>
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
}

export default function Duels() {
  const { user, refresh } = useAuth();
  const { sfx } = useAudio();
  const { t } = useT();
  const [params, setParams] = useSearchParams();
  const cashOn = !!user?.cash_enabled;
  const STAKES = cashOn ? [1, 5, 10, 25] : [100, 500, 1000, 2500];
  const fmt = (v) => (cashOn ? `$${v}` : `${Number(v).toLocaleString()} coins`);
  const balance = cashOn ? (user?.cash ?? 0) : (user?.coins ?? 0);
  const [stake, setStake] = useState(cashOn ? 5 : 500);
  const [gameType, setGameType] = useState("speed_math");
  const [ladder, setLadder] = useState([]);
  const [history, setHistory] = useState([]);
  const [active, setActive] = useState(null); // {duel_id, opponent}
  const [searching, setSearching] = useState(false);
  const [searchDuel, setSearchDuel] = useState(null);
  const [myCh, setMyCh] = useState([]);
  const [showDaily, setShowDaily] = useState(false);
  const [showSolDaily, setShowSolDaily] = useState(false);

  const load = async () => {
    try {
      const [lb, h, mc] = await Promise.all([api.get("/duels/leaderboard"), api.get("/duels/history"), api.get("/duels/my-challenges")]);
      setLadder(lb.data); setHistory(h.data); setMyCh(mc.data);
    } catch { /* ignore */ }
  };
  const cancelChallenge = async (code) => {
    try { const { data } = await api.post(`/duels/challenge/${code}/cancel`); await refresh(); load(); toast.success(`Refunded ${fmt(data.refund)}`); }
    catch (e) { toast.error(apiErr(e)); }
  };
  useEffect(() => { load(); }, []);

  const eligible = cashOn ? (user?.geo_allowed_cash && user?.age_verified) : true;

  // Accept an incoming friend-challenge link (?challenge=CODE)
  useEffect(() => {
    const code = params.get("challenge");
    if (!code || !user) return;
    (async () => {
      try {
        const { data } = await api.post(`/duels/challenge/${code}/accept`);
        await refresh();
        setActive({ duel_id: data.duel_id, opponent: data.opponent });
        toast.success("Challenge accepted!");
      } catch (e) { toast.error(apiErr(e)); }
      finally { setParams({}, { replace: true }); }
    })();
  }, [params, user]);

  // Free-play launch from the PhonePlay arcade (?practice=<game>)
  useEffect(() => {
    const g = params.get("practice");
    if (!g || !user) return;
    setActive({ duel_id: null, opponent: { name: "Practice", elo: "—" }, practice: true, game: g });
    setParams({}, { replace: true });
  }, [params, user]);

  const searchRef = useRef(null);
  const find = async (s = stake, game = gameType) => {
    setSearching(true);
    try {
      const { data } = await api.post("/duels/find", { stake: s, game });
      await refresh();
      if (data.matched) {
        setSearching(false); sfx("whoosh");
        setActive({ duel_id: data.duel_id, opponent: data.opponent });
        return;
      }
      // Queued for a REAL opponent — poll until someone joins (cancelable).
      setSearchDuel(data.duel_id);
      searchRef.current = setInterval(async () => {
        try {
          const { data: st } = await api.get(`/duels/${data.duel_id}/status`);
          if (st.matched) {
            clearInterval(searchRef.current);
            setSearching(false); setSearchDuel(null); sfx("whoosh");
            setActive({ duel_id: data.duel_id, opponent: st.opponent });
          }
        } catch { /* keep polling */ }
      }, 3000);
    } catch (e) { toast.error(apiErr(e)); setSearching(false); }
  };
  const cancelSearch = async () => {
    if (searchRef.current) clearInterval(searchRef.current);
    try { if (searchDuel) { const { data } = await api.post(`/duels/${searchDuel}/cancel-search`); await refresh(); toast.success(`Search cancelled · ${fmt(data.refund)} refunded`); } }
    catch (e) { toast.error(apiErr(e)); }
    finally { setSearching(false); setSearchDuel(null); }
  };
  useEffect(() => () => { if (searchRef.current) clearInterval(searchRef.current); }, []);

  const createChallenge = async () => {
    try {
      const { data } = await api.post("/duels/challenge", { stake, game: gameType });
      const link = `${window.location.origin}/duels?challenge=${data.code}`;
      try { await navigator.clipboard.writeText(link); } catch { /* ignore */ }
      await refresh();
      toast.success(`${t("link_copied")} (${data.code})`, { description: link, duration: 8000 });
    } catch (e) { toast.error(apiErr(e)); }
  };

  const rematch = () => { setActive(null); setTimeout(() => find(stake, gameType), 300); };

  return (
    <div className="p-4 lg:p-8">
      <div className={`relative rounded-3xl overflow-hidden mb-8 border ${cashOn ? "border-emerald-500/20 glow-cash" : "border-amber-500/20 glow-coins"}`}>
        <img decoding="async" fetchPriority="high" src="https://images.unsplash.com/photo-1636487658582-96efd1693bcb?crop=entropy&cs=srgb&fm=jpg&q=70&w=1200" alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/70 to-transparent" />
        <div className="relative z-10 p-6 lg:p-12">
          <span data-testid="duels-mode-badge" className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold mb-4 ${cashOn ? "bg-emerald-500/15 border border-emerald-500/30 text-emerald-300" : "bg-amber-500/15 border border-amber-500/30 text-amber-300"}`}>
            <Swords className="w-3.5 h-3.5" /> {cashOn ? "1v1 · Cash Stakes · ELO" : "1v1 · Fun Coin Stakes · ELO · No monetary value"}
          </span>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight mb-3">
            <span className={cashOn ? "text-gradient-cash" : "text-gradient-coins"}>{t("duels_title")}</span>
          </h1>
          <p className="text-gray-400 max-w-xl">{cashOn ? t("duels_sub") : t("duels_sub_coins")}</p>
        </div>
      </div>

      {cashOn && !eligible && (
        <div className="glass rounded-2xl p-5 border border-red-500/30 mb-6 flex items-start gap-3">
          <Lock className="w-5 h-5 text-red-400 mt-0.5" />
          <p className="text-sm text-gray-400">Cash duels require a cash-eligible region and age verification.</p>
        </div>
      )}

      <button data-testid="daily-challenge-card" onClick={() => setShowDaily(true)}
        className="w-full mb-6 text-left rounded-3xl overflow-hidden border border-amber-500/30 bg-gradient-to-r from-amber-500/10 via-purple-600/10 to-transparent hover:border-amber-500/50 transition-colors duration-200 p-5 lg:p-6 flex items-center gap-4 group">
        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-400 to-purple-600 flex items-center justify-center shrink-0">
          <CalendarDays className="w-7 h-7 text-white" />
        </div>
        <div className="flex-1">
          <p className="font-display text-lg font-black flex items-center gap-2">Daily WordVolt Challenge <Sparkles className="w-4 h-4 text-amber-400" /></p>
          <p className="text-sm text-gray-400">One free puzzle a day · same letters for everyone · climb the global leaderboard.</p>
        </div>
        <span className="hidden sm:inline-flex px-4 py-2 rounded-xl bg-gradient-to-r from-amber-500 to-purple-600 font-semibold text-sm group-hover:opacity-90">Play today</span>
      </button>

      <button data-testid="daily-solitaire-card" onClick={() => setShowSolDaily(true)}
        className="w-full mb-6 text-left rounded-3xl overflow-hidden border border-amber-500/30 bg-gradient-to-r from-amber-500/10 via-purple-600/10 to-transparent hover:border-amber-500/50 transition-colors duration-200 p-5 lg:p-6 flex items-center gap-4 group">
        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-400 to-purple-600 flex items-center justify-center shrink-0">
          <Layers className="w-7 h-7 text-white" />
        </div>
        <div className="flex-1">
          <p className="font-display text-lg font-black flex items-center gap-2">Daily Solitaire Sprint <Sparkles className="w-4 h-4 text-amber-400" /></p>
          <p className="text-sm text-gray-400">One free deal a day · same 24 cards for everyone · build the longest chain.</p>
        </div>
        <span className="hidden sm:inline-flex px-4 py-2 rounded-xl bg-gradient-to-r from-amber-500 to-purple-600 font-semibold text-sm group-hover:opacity-90">Play today</span>
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Matchmaking */}
        <div className="glass rounded-2xl p-6 border border-emerald-500/20">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display text-xl font-bold flex items-center gap-2"><Zap className="w-5 h-5 text-emerald-400" /> {t("find_match")}</h3>
            <div className="text-right"><p className="text-xs text-gray-500">{t("your_elo")}</p><p className="font-mono font-black text-emerald-300">{user?.elo ?? 1000}</p></div>
          </div>
          <label className="text-sm text-gray-400 mb-2 block">Game</label>
          <div className="grid grid-cols-2 gap-2 mb-4">
            {[["speed_math", "Speed Math"], ["word_volt", "WordVolt"], ["solitaire_sprint", "Solitaire Sprint"], ["reaction_rush", "Reaction Rush"], ["memory_matrix", "Memory Matrix"]].map(([g, label]) => (
              <button key={g} onClick={() => setGameType(g)} data-testid={`duel-game-${g}`}
                className={`py-2 rounded-lg text-xs font-semibold transition-colors duration-200 ${gameType === g ? "bg-gradient-to-r from-emerald-500 to-cyan-600 text-white" : "bg-white/5 text-gray-400 hover:text-white"}`}>{label}</button>
            ))}
          </div>
          <label className="text-sm text-gray-400 mb-2 block">{t("stake")} ({cashOn ? "$" : "Fun Coins"})</label>
          <div className="flex gap-2 mb-4">
            {STAKES.map((s) => (
              <button key={s} onClick={() => setStake(s)} data-testid={`duel-stake-${s}`}
                className={`flex-1 py-2 rounded-lg font-mono text-sm transition-colors duration-200 ${stake === s ? "bg-gradient-to-r from-emerald-500 to-cyan-600 text-white" : "bg-white/5 text-gray-400 hover:text-white"}`}>{cashOn ? `$${s}` : s.toLocaleString()}</button>
            ))}
          </div>
          <button data-testid="find-match-button" onClick={() => find()} disabled={!eligible || searching || balance < stake}
            className="w-full py-3.5 rounded-xl bg-gradient-to-r from-emerald-500 to-cyan-600 font-bold hover:opacity-90 transition-opacity duration-200 disabled:opacity-40">
            {searching ? "Searching for a real opponent…" : `${t("find_match")} · ${fmt(stake)}`}
          </button>
          {searching && (
            <div data-testid="searching-panel" className="mt-2 text-center">
              <p className="text-xs text-gray-500 mb-2">Duels are real players only — no bots. Your {fmt(stake)} is held and fully refunded if you cancel.</p>
              <button data-testid="cancel-search-button" onClick={cancelSearch}
                className="w-full py-2.5 rounded-xl bg-red-500/15 border border-red-500/30 text-red-300 font-semibold text-sm hover:bg-red-500/25 transition-colors duration-200">
                Cancel search &amp; refund
              </button>
            </div>
          )}
          <button data-testid="challenge-friend-button" onClick={createChallenge} disabled={!eligible || balance < stake}
            className="w-full mt-2 py-2.5 rounded-xl bg-white/5 border border-white/10 font-semibold text-sm hover:bg-white/10 transition-colors duration-200 flex items-center justify-center gap-2 disabled:opacity-40">
            <Share2 className="w-4 h-4" /> {t("challenge_friend")}
          </button>
          <button data-testid="practice-button" onClick={() => setActive({ duel_id: null, opponent: { name: "Practice", elo: "—" }, practice: true, game: gameType })}
            className="w-full mt-2 py-2.5 rounded-xl bg-amber-500/10 border border-amber-500/30 text-amber-300 font-semibold text-sm hover:bg-amber-500/20 transition-colors duration-200">
            Practice free — no stake
          </button>
          {balance < stake && eligible && <p className="text-xs text-amber-400 mt-2">{cashOn ? "Add cash in your Wallet to play this stake." : "Not enough Fun Coins for this stake — claim your daily bonus or visit the Coin Store."}</p>}
          {myCh.length > 0 && (
            <div className="mt-4 pt-4 border-t border-white/10">
              <p className="text-xs text-gray-500 mb-2">Open challenges</p>
              {myCh.map((c) => (
                <div key={c.code} className="flex items-center justify-between text-sm py-1">
                  <span className="font-mono">{c.code} · {c.currency === "coins" ? `${Number(c.stake).toLocaleString()} coins` : `$${c.stake}`}</span>
                  <button data-testid={`cancel-challenge-${c.code}`} onClick={() => cancelChallenge(c.code)} className="text-red-400 hover:text-red-300 text-xs">Cancel & refund</button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ELO ladder */}
        <div className="glass rounded-2xl p-6 border border-white/10">
          <h3 className="font-display text-xl font-bold mb-4 flex items-center gap-2"><Crown className="w-5 h-5 text-amber-400" /> {t("elo_ladder")}</h3>
          <div className="space-y-1.5 max-h-72 overflow-y-auto hide-scroll">
            {ladder.map((p, i) => (
              <div key={p.user_id} className={`flex items-center justify-between py-1.5 px-2 rounded-lg ${p.user_id === user?.user_id ? "bg-emerald-500/10" : ""}`}>
                <span className="text-sm">#{i + 1} {p.name}{p.user_id === user?.user_id ? " (you)" : ""}</span>
                <span className="font-mono text-emerald-300 text-sm">{p.elo}</span>
              </div>
            ))}
          </div>
        </div>

        {/* History */}
        <div className="glass rounded-2xl p-6 border border-white/10">
          <h3 className="font-display text-xl font-bold mb-4 flex items-center gap-2"><Trophy className="w-5 h-5 text-emerald-400" /> {t("recent_duels")}</h3>
          <div className="space-y-2 max-h-72 overflow-y-auto hide-scroll">
            {history.map((d) => (
              <div key={d.duel_id} className="flex items-center justify-between text-sm py-1.5 border-b border-white/5">
                <span className="flex items-center gap-2">
                  {d.outcome === "win" ? <TrendingUp className="w-4 h-4 text-emerald-400" /> : d.outcome === "draw" ? <span className="w-4 text-center">—</span> : <TrendingDown className="w-4 h-4 text-red-400" />}
                  vs {d.opponent}
                </span>
                <span className="font-mono text-gray-400">{d.your_score}-{d.opp_score}{d.outcome === "win" ? (d.currency === "coins" ? ` · +${Number(d.prize).toLocaleString()} coins` : ` · +$${d.prize}`) : ""}</span>
              </div>
            ))}
            {history.length === 0 && <p className="text-gray-600 text-sm">No duels yet. Find your first match!</p>}
          </div>
        </div>
      </div>

      <AnimatePresence>
        {showDaily && <DailyWordVolt onClose={() => { setShowDaily(false); load(); }} />}
        {showSolDaily && <DailySolitaire onClose={() => { setShowSolDaily(false); load(); }} />}
        {active && <DuelArena duel={active.duel_id} opponent={active.opponent} practice={active.practice} game={active.game} onClose={() => { setActive(null); load(); }} refresh={refresh} onRematch={rematch} onChallenge={createChallenge} />}
      </AnimatePresence>
    </div>
  );
}
